/*************************
 * Author : Matthieu ROBIN
 *************************/

import React, { FunctionComponent, Key } from "react";

import {
  Card,
  CardMedia,
  CardActionArea,
  CardContent,
  Typography,
  Chip,
  CardHeader,
  Box,
  IconButton,
  Stack,
  TableRow,
  TableCell,
  Link,
} from "@mui/material";
import { useNavigate } from "react-router";
import { Car, CarPosition, CarStatus } from "../models/car";
import {
  EmojiEvents,
  Factory,
  MoreVert,
  SportsMotorsports,
} from "@mui/icons-material";
import placeholder from "../images/car_placeholder.png";

type CarRowProps = {
  car: Car;
};

const getChipColor = (status: CarStatus) => {
  switch (status) {
    case "Nouveau":
      return "#4DD091";
    case "Notable":
      return "#5f6f52";
    case "Souhaité":
      return "#B980FF";
    case "Réservé":
      return "#E6C84F";
    case "A garder":
      return "#00A5E3";
    case "A vendre":
      return "#FFA23A";
    case "En vente":
      return "#FF6F68";
    case "Vendu":
      return "#74737A";
  }
};

const getPositionIcon = (position: CarPosition) => {
  switch (position) {
    case "1st":
      return <EmojiEvents htmlColor="#FFD700" />;
    case "2nd":
      return <EmojiEvents htmlColor="#BCC6CC" />;
    case "3rd":
      return <EmojiEvents htmlColor="#CD7F32" />;
  }
};

const CarRow: FunctionComponent<CarRowProps> = ({ car }) => {
  const navigate = useNavigate();

  const goToCar = (id: string | undefined) => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryParams = searchParams.toString()
      ? `?${searchParams.toString()}`
      : "";
    navigate(`/cars/${id}${queryParams}`);
  };

  const netSalePrice: number | undefined =
    car.purchasePrice && car.status === "Vendu"
      ? car.sell.trueSellPrice +
        car.sell.shippingCosts -
        car.sell.trueShippingCosts -
        car.sell.fees
      : undefined;

  const gain: number | undefined =
    car.purchasePrice && car.status === "Vendu"
      ? netSalePrice - car.purchasePrice
      : undefined;

  const logoName = car.brand.toLocaleLowerCase().replace(" ", "-");

  const title = [
    car.brand,
    car.type,
    car.number ? `#${car.number}` : undefined,
  ].join(" ");

  const subheader = [car.competition, car.year].join(" ");

  return (
    <TableRow>
      <TableCell>
        <Box>
          <Chip
            label={car.status}
            style={{
              borderRadius: 5,
              backgroundColor: getChipColor(car.status),
              color: "white",
            }}
            size="small"
          />
        </Box>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <img
            src={`/logos/${logoName}.svg`}
            height={15}
            width={25}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = "/logos/unknown.svg";
            }}
          />
          <Link
            style={{ cursor: "pointer" }}
            underline="none"
            onClick={() => goToCar(car._id)}
          >
            {title}
          </Link>
        </Stack>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          {subheader}
          {getPositionIcon(car.position)}
        </Stack>
      </TableCell>
      <TableCell>{car.pilots.join(", ")}</TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography>{car.manufacturer} </Typography>
          <Typography variant="caption">{car.reference}</Typography>
        </Stack>
      </TableCell>
      <TableCell>
        {car.purchasePrice != undefined ? car.purchasePrice.toFixed(2) : "-"}
      </TableCell>
      <TableCell>
        {netSalePrice != undefined ? netSalePrice.toFixed(2) : "-"}
      </TableCell>
      <TableCell
        style={{
          color: gain != undefined ? (gain >= 0 ? "green" : "red") : "inherit",
        }}
      >
        {gain != undefined ? gain.toFixed(2) : "-"}
      </TableCell>
    </TableRow>
  );
};

export default CarRow;
